import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureEntityRef } from '../types/measure-types';
import { MeasureAnchorMarker } from './MeasureAnchorMarker';

/**
 * 测量标注 3D 渲染器。
 *
 * 职责：
 * 1. 维护测量 overlay 根节点与锚点精灵集合；
 * 2. 双选时始终可显示两端最近点锚点；单选“信息锚点”仅在点到点模式下由上层传入；
 * 3. 会话结束时统一释放 GPU 资源。
 */
export class MeasureCalloutRenderer {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /**
     * 测量 overlay 根节点，作为所有锚点标记（activeAnchor, pairAnchorFrom, pairAnchorTo 等）的父对象。
     * 本质上是一个仅用于层级管理的 THREE.Group 容器，不参与渲染逻辑，只用于挂载/组织锚点精灵对象。
     */
    private overlayRoot: THREE.Group | null = null;

    /** 当前唯一显示的单选锚点标记。 */
    private activeAnchor: MeasureAnchorMarker | null = null;

    /** 双选测量：第一个实体上的锚点。 */
    private pairAnchorFrom: MeasureAnchorMarker | null = null;

    /** 双选测量：第二个实体上的锚点。 */
    private pairAnchorTo: MeasureAnchorMarker | null = null;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 设置当前唯一显示的单选测量锚点。
     *
     * @param entity 活动锚点实体；为 null 时隐藏单选锚点。
     */
    public setActiveAnchor(entity: MeasureEntityRef | null): void {
        this.ensureOverlayRoot();

        if (!entity) {
            this.activeAnchor?.dispose();
            this.activeAnchor = null;
            this.updateOverlayVisibility();
            this.viewer.requestRender?.();
            return;
        }

        if (this.activeAnchor) {
            this.activeAnchor.setWorldPosition(entity.worldPickPoint);
        } else {
            const marker = new MeasureAnchorMarker(entity.worldPickPoint);
            marker.bindScreenSizeContext(
                () => this.viewer.camera,
                () => this.viewer.container,
            );
            this.overlayRoot?.add(marker);
            this.activeAnchor = marker;
        }

        this.updateOverlayVisibility();
        this.viewer.requestRender?.();
    }

    /**
     * 设置双选测量的两个锚点（最近点对）。
     *
     * @param from 第一个实体上的最近点；为 null 时清除双选锚点。
     * @param to 第二个实体上的最近点。
     */
    public setPairAnchors(from: THREE.Vector3 | null, to: THREE.Vector3 | null): void {
        this.ensureOverlayRoot();

        if (!from || !to) {
            this.pairAnchorFrom?.dispose();
            this.pairAnchorTo?.dispose();
            this.pairAnchorFrom = null;
            this.pairAnchorTo = null;
            this.updateOverlayVisibility();
            this.viewer.requestRender?.();
            return;
        }

        this.pairAnchorFrom = this.upsertPairAnchor(this.pairAnchorFrom, 'from', from);
        this.pairAnchorTo = this.upsertPairAnchor(this.pairAnchorTo, 'to', to);

        this.updateOverlayVisibility();
        this.viewer.requestRender?.();
    }

    /**
     * 清空全部锚点并隐藏 overlay。
     */
    public clearAnchors(): void {
        this.activeAnchor?.dispose();
        this.activeAnchor = null;
        this.pairAnchorFrom?.dispose();
        this.pairAnchorTo?.dispose();
        this.pairAnchorFrom = null;
        this.pairAnchorTo = null;
        if (this.overlayRoot) {
            this.overlayRoot.visible = false;
        }
        this.viewer.requestRender?.();
    }

    /**
     * 释放 overlay 与全部锚点资源。
     */
    public dispose(): void {
        this.clearAnchors();
        if (this.overlayRoot) {
            if (this.overlayRoot.parent) {
                this.overlayRoot.parent.remove(this.overlayRoot);
            }
            this.overlayRoot = null;
        }
    }

    /**
     * 更新或创建双选锚点。
     *
     * @param existing 已有锚点。
     * @param slotKey 槽位标识（仅用于命名）。
     * @param worldPosition 世界坐标。
     * @returns 锚点实例。
     */
    private upsertPairAnchor(
        existing: MeasureAnchorMarker | null,
        slotKey: string,
        worldPosition: THREE.Vector3,
    ): MeasureAnchorMarker {
        if (existing) {
            existing.setWorldPosition(worldPosition);
            return existing;
        }

        const marker = new MeasureAnchorMarker(worldPosition, slotKey);
        marker.bindScreenSizeContext(
            () => this.viewer.camera,
            () => this.viewer.container,
        );
        this.overlayRoot?.add(marker);
        return marker;
    }

    /**
     * 按当前锚点数量更新 overlay 可见性。
     */
    private updateOverlayVisibility(): void {
        if (!this.overlayRoot) {
            return;
        }
        const hasAnyAnchor = this.activeAnchor !== null
            || this.pairAnchorFrom !== null
            || this.pairAnchorTo !== null;
        this.overlayRoot.visible = hasAnyAnchor;
    }

    /**
     * 确保测量 overlay 根节点已挂载到场景。
     */
    private ensureOverlayRoot(): void {
        if (this.overlayRoot) {
            return;
        }

        const overlayRoot = new THREE.Group();
        overlayRoot.name = 'MeasureCalloutOverlayRoot';
        overlayRoot.visible = false;
        overlayRoot.userData.skipSelection = true;
        overlayRoot.userData.skipSelectionSubtree = true;
        this.viewer.addObject(overlayRoot);
        this.overlayRoot = overlayRoot;
    }
}
